import { FloatingPanel, Portal, useFloatingPanel } from "@ark-ui/react";
import { type Organization, OrganizationSchema } from "@packages/core";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ReactNode, useEffect, useMemo, useRef } from "react";
import { LuBuilding, LuCircleX } from "react-icons/lu";
import { type Merge } from "type-fest";
import * as v from "valibot";
import { useTranslate } from "../../services";
import { css, cx } from "../../styled-system/css";
import {
  FloatingPanelBorder,
  FloatingPanelHeader,
  ellipsis,
  floatingPanelStyles,
  formFieldBoxStyles,
  iconButtonStyles,
  skeletonStyle,
} from "../common";
import { showError } from "../utilities";

export type OrganizationSelectorProperties = Merge<
  FloatingPanel.TriggerProps,
  {
    load?: { (): Promise<void> };
    loaded?: boolean;
    loading?: boolean;
    onBlur?: { (): void };
    onChange: { (organization: Organization | null): void };
    options: ReadonlyArray<Organization>;
    title?: ReactNode;
    value: Organization<boolean> | null;
  }
>;

export function OrganizationSelector({
  className,
  load,
  loaded,
  loading,
  onBlur,
  onChange,
  options,
  title,
  value,
  ...properties
}: OrganizationSelectorProperties) {
  const classes = floatingPanelStyles();
  const floatingPanel = useFloatingPanel();
  const container = useRef<HTMLDivElement>(null);
  const promise = useRef<Promise<void> | undefined>(undefined);
  const translate = useTranslate();

  const organization = useMemo(
    function () {
      try {
        if (value) {
          return v.parse(OrganizationSchema(), value);
        } else {
          return null;
        }
      } catch (error) {
        showError(error);
        return null;
      }
    },
    [value],
  );

  const virtualizer = useVirtualizer({
    count: loaded ? options.length : options.length + 5,
    getScrollElement: () => container.current,
    estimateSize: () => 56,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(
    function () {
      const lastItem = virtualItems.at(-1);
      if (
        !promise.current &&
        load &&
        !loaded &&
        !loading &&
        lastItem !== undefined &&
        lastItem.index >= options.length - 1
      ) {
        // 重複して読み込まれないようにする。
        promise.current = load().then(function () {
          promise.current = undefined;
        });
      }
    },

    [load, loaded, loading, options, virtualItems],
  );

  return (
    <FloatingPanel.RootProvider value={floatingPanel}>
      <FloatingPanel.Trigger
        {...properties}
        className={cx(
          css(formFieldBoxStyles.raw(), {
            alignItems: "center",
            cursor: "pointer",
            display: "flex",
            paddingInline: "8px",
          }),
          className,
        )}
      >
        {organization ? (
          <div
            className={css({ alignItems: "center", display: "flex", gap: "2px", width: "100%" })}
          >
            <div>
              <LuBuilding className={css({ height: "16px", width: "auto" })} />
            </div>
            <p className={css(ellipsis, { fontSize: "14px", lineHeight: 1 })}>
              {organization.name}
            </p>
            <p
              className={css(ellipsis, {
                color: "muted-foreground",
                fontSize: "12px",
                lineHeight: 1,
              })}
            >
              {organization?.address}
            </p>
            <div className={css({ flex: 1 })} />
            <div
              className={iconButtonStyles()}
              onClick={function (event) {
                event.preventDefault();
                event.stopPropagation();
                onChange(null);
              }}
            >
              <LuCircleX className={css({ height: "16px", width: "auto" })} />
            </div>
          </div>
        ) : (
          <span
            className={css(ellipsis, {
              color: "muted-foreground",
              fontSize: "14px",
              lineHeight: 1,
            })}
          >
            {translate("%ClickToSelectOrganization.0%")}
          </span>
        )}
      </FloatingPanel.Trigger>
      <Portal>
        <FloatingPanel.Positioner>
          <FloatingPanel.Content className={classes.content}>
            <FloatingPanelHeader title={title} />
            <FloatingPanel.Body className={classes.body}>
              <div className={css({ flex: 1, height: "100%", overflow: "auto" })} ref={container}>
                <div
                  className={css({ position: "relative" })}
                  style={{ height: virtualizer.getTotalSize() + "px" }}
                >
                  {virtualItems.map(function (virtualItem) {
                    const option = options.at(virtualItem.index);
                    return (
                      <div
                        className={css({
                          alignItems: "center",
                          cursor: "pointer",
                          display: "flex",
                          position: "absolute",
                          paddingX: "8px",
                          width: "100%",
                          _hover: { backgroundColor: "black/10" },
                          _focus: { backgroundColor: "black/20" },
                          _active: { backgroundColor: "black/30" },
                        })}
                        data-index={virtualItem.index}
                        key={virtualItem.key}
                        onClick={function () {
                          if (option) {
                            onChange(option);
                            floatingPanel.setOpen(false);
                          }
                        }}
                        style={{
                          height: virtualItem.size + "px",
                          transform: "translateY(" + virtualItem.start + "px)",
                        }}
                      >
                        {option ? (
                          <div>
                            <p className={css({ fontWeight: 500 })}>
                              <span>{option.name}&nbsp;</span>
                              <span className={css({ color: "primary", fontSize: "10px" })}>
                                ({option.organizationKey})
                              </span>
                            </p>
                            <p className={css({ fontSize: "12px", fontWeight: 500 })}>
                              {option.address}
                            </p>
                          </div>
                        ) : (
                          <div>
                            <div
                              className={css(skeletonStyle, { height: "1em", width: "128px" })}
                            />
                            <div
                              className={css(skeletonStyle, { height: "12px", width: "256px" })}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {loaded && options.length === 0 && (
                  <p className={css({ padding: "16px" })}>{translate("%NoOrganizations.0%")}</p>
                )}
              </div>
            </FloatingPanel.Body>
            <FloatingPanelBorder />
          </FloatingPanel.Content>
        </FloatingPanel.Positioner>
      </Portal>
    </FloatingPanel.RootProvider>
  );
}
